/**
 * <s-tilt-card> — 3D tilt for a single featured image (homepage Tee of the week).
 *
 * On fine pointers, writes --px/--py (-1..1, pointer position from centre) and
 * --gx/--gy (glare position, %) on the element; CSS turns them into rotation,
 * parallax and glare. Sets data-active while the pointer is over it.
 * On every device, sets data-armed on mount and data-inview once it scrolls
 * into view, so CSS can play a one-shot 3D settle-in.
 * Does nothing when the visitor prefers reduced motion.
 */
class STiltCard extends HTMLElement {
  #abort;
  #observer;
  #frame = 0;

  connectedCallback() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.dataset.armed = '';
    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.dataset.inview = '';
        this.#observer?.disconnect();
      },
      { threshold: 0.25 },
    );
    this.#observer.observe(this);

    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.addEventListener('pointerenter', () => { this.dataset.active = ''; }, { signal });
    this.addEventListener('pointermove', (event) => this.#track(event), { signal });
    this.addEventListener('pointerleave', () => this.#reset(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  #track(event) {
    const rect = this.getBoundingClientRect();
    const x = (event.clientX - rect.left) / rect.width;
    const y = (event.clientY - rect.top) / rect.height;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      this.style.setProperty('--px', ((x - 0.5) * 2).toFixed(3));
      this.style.setProperty('--py', ((y - 0.5) * 2).toFixed(3));
      this.style.setProperty('--gx', `${(x * 100).toFixed(1)}%`);
      this.style.setProperty('--gy', `${(y * 100).toFixed(1)}%`);
    });
  }

  #reset() {
    cancelAnimationFrame(this.#frame);
    delete this.dataset.active;
    this.style.setProperty('--px', '0');
    this.style.setProperty('--py', '0');
  }
}

customElements.define('s-tilt-card', STiltCard);
